import { startCapture } from './audio/capture.js';
import { PcmPlayer } from './audio/playback.js';
import { decode, encode, isServerFrame, } from './wire.js';
export class VoiceClientError extends Error {
    code;
    constructor(code, message, options) {
        super(message, options);
        this.code = code;
        this.name = 'VoiceClientError';
    }
}
export class VoiceClient {
    options;
    listeners = new Map();
    player = new PcmPlayer();
    socket;
    startPromise;
    capture;
    textEnabled = false;
    startedAt;
    stoppedAt;
    ended = false;
    endRequested = false;
    constructor(options) {
        this.options = options;
    }
    startFromGesture() {
        if (this.socket?.readyState === WebSocket.OPEN)
            return Promise.resolve();
        if (this.startPromise)
            return this.startPromise;
        this.startPromise = this.start().finally(() => {
            this.startPromise = undefined;
        });
        return this.startPromise;
    }
    async start() {
        const playbackReady = this.player.unlock();
        const captureReady = startCapture((chunk) => this.sendAudio(chunk));
        try {
            const capture = await captureReady;
            this.capture = capture;
            await playbackReady;
            await this.connect();
            this.startedAt = Date.now();
            this.stoppedAt = undefined;
            this.ended = false;
            this.endRequested = false;
            this.send({ type: 'start', target: this.options.target });
        }
        catch (error) {
            this.stopCapture();
            this.player.close();
            const mapped = this.startError(error);
            this.emit({ v: 1, type: 'error', code: mapped.code, message: mapped.message });
            throw mapped;
        }
    }
    end() {
        this.endRequested = true;
        if (this.socket?.readyState === WebSocket.OPEN)
            this.send({ type: 'end' });
        this.stopCapture();
        this.player.close();
    }
    sendText(text) {
        if (this.textEnabled)
            this.send({ type: 'text', text });
    }
    confirmDraft(draftId, editedText) {
        this.send({
            type: 'confirm_draft',
            draft_id: draftId,
            ...(editedText === undefined ? {} : { edited_text: editedText }),
        });
    }
    discardDraft(draftId) {
        this.send({ type: 'discard_draft', draft_id: draftId });
    }
    on(type, listener) {
        const listeners = this.listeners.get(type) ?? new Set();
        listeners.add(listener);
        this.listeners.set(type, listeners);
        return () => listeners.delete(listener);
    }
    minutes() {
        if (this.startedAt === undefined)
            return 0;
        return ((this.stoppedAt ?? Date.now()) - this.startedAt) / 60_000;
    }
    connect() {
        return new Promise((resolve, reject) => {
            const url = new URL(this.options.url);
            url.searchParams.set('token', this.options.token);
            const socket = new WebSocket(url);
            this.socket = socket;
            let opened = false;
            socket.addEventListener('open', () => {
                opened = true;
                resolve();
            }, { once: true });
            socket.addEventListener('error', () => {
                if (!opened) {
                    if (this.socket === socket)
                        this.socket = undefined;
                    reject(new VoiceClientError('socket_error', 'Voice connection failed.'));
                }
            }, { once: true });
            socket.addEventListener('message', (event) => this.receive(event));
            socket.addEventListener('close', (event) => {
                if (!opened) {
                    if (this.socket === socket)
                        this.socket = undefined;
                    reject(event.code === 4401
                        ? new VoiceClientError('unauthorized', 'Voice connection was not authorized.')
                        : new VoiceClientError('socket_error', 'Voice connection was rejected.'));
                    return;
                }
                this.handleClose(socket, event.code);
            });
        });
    }
    receive(event) {
        if (typeof event.data !== 'string') {
            this.emitInvalidFrame('Voice socket sent a non-text frame.');
            return;
        }
        try {
            const frame = decode(event.data);
            if (!isServerFrame(frame)) {
                this.emitInvalidFrame('Voice socket sent a client-only frame.');
                return;
            }
            this.handleFrame(frame);
        }
        catch (error) {
            this.emitInvalidFrame(error instanceof Error ? error.message : 'Invalid voice frame.');
        }
    }
    handleFrame(frame) {
        if (frame.type === 'agent_audio')
            this.player.enqueue(frame);
        if (frame.type === 'interrupted')
            this.player.stop();
        if (frame.type === 'state') {
            const capabilities = frame.capabilities;
            this.textEnabled =
                Boolean(capabilities) &&
                    typeof capabilities === 'object' &&
                    capabilities.text === true;
        }
        if (frame.type === 'ended') {
            this.ended = true;
            this.finishTimer();
            this.stopCapture();
            this.player.close();
        }
        this.emit(frame);
    }
    handleClose(socket, code) {
        if (this.socket !== socket)
            return;
        this.finishTimer();
        this.stopCapture();
        this.player.close();
        this.socket = undefined;
        if (code === 4401) {
            this.emit({
                v: 1,
                type: 'error',
                code: 'unauthorized',
                message: 'Voice connection was not authorized.',
            });
            return;
        }
        if (!this.ended) {
            this.ended = true;
            this.emit({
                v: 1,
                type: 'ended',
                reason: this.endRequested ? 'user_end' : 'transport_lost',
            });
        }
    }
    sendAudio(dataBase64) {
        if (this.socket?.readyState === WebSocket.OPEN) {
            this.send({ type: 'audio', data_b64: dataBase64 });
        }
    }
    send(frame) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            throw new VoiceClientError('not_connected', 'Voice socket is not connected.');
        }
        this.socket.send(encode(frame));
    }
    emit(frame) {
        for (const listener of this.listeners.get(frame.type) ?? []) {
            listener(frame);
        }
    }
    emitInvalidFrame(message) {
        this.emit({ v: 1, type: 'error', code: 'invalid_frame', message });
    }
    stopCapture() {
        this.capture?.stop();
        this.capture = undefined;
    }
    finishTimer() {
        if (this.startedAt !== undefined && this.stoppedAt === undefined)
            this.stoppedAt = Date.now();
    }
    startError(error) {
        if (error instanceof VoiceClientError)
            return error;
        if (error instanceof DOMException && error.name === 'NotAllowedError') {
            return new VoiceClientError('mic_denied', 'Microphone permission denied.', { cause: error });
        }
        return new VoiceClientError('start_failed', 'Voice session could not start.', { cause: error });
    }
}
//# sourceMappingURL=client.js.map